import { useRef, useState } from 'react';
import { formatDate } from '../dates.ts';
import { asSentence } from '../sentence.ts';
import { Refusal } from './PropertiesPanel.tsx';
import { RecordEdit } from './RecordEdit.ts';
import { useClientFolders } from '../api/uploads.ts';
import { ReasonFields, useReasonReady } from './VisitDetails.tsx';
import { MoneyBox } from './MoneyBox.tsx';
import { formatMoneyInput, moneyText } from '../moneyInput.ts';
import {
  ACCOUNT_CORRECTION_SAYS, ADD_BANK_LABEL, COPIES_WORDS, KIND_WORDS, NEW_BANK_HINT, NEW_BANK_LABEL, STEP_HINTS, STEP_ORDER,
  STEP_WORDS, addBankPlace, copiesOk, deadlineSaid, institutionState, lastFour, stepCorrectionSays, stepFields, stepOrderSaid,
} from '../marshalling.ts';
import {
  useAddAccount, useAddInstitution, useClientMarshalling, useCorrectAccount, useCorrectStep, useNoAccountsFound, useRecordStep,
  useWithdrawInstitution, useWithdrawStep,
  type AccountKind, type AccountOutcome, type ClientMarshalling, type MarshalAccount, type MarshalInstitution,
  type MarshalStep, type StepKind,
} from '../api/marshalling.ts';

/**
 * "Accounts and marshalling", the Finance section (166, P-33; Arthur, D-114, D-122, A-#352, A-#354; the advisor A-#391).
 * Each bank holding the client's money is marshalled by its own dated steps; the database decides when a bank is
 * marshalled (its last check in hand, its statements, or a move into a guardianship account at the bank; a partial up
 * to an order counts), and "Marshal client accounts" completes itself when every bank still counted is. Accounts are
 * shown by their last four only. Who records: the server's may_record (the client's Finance case manager, AP/AR, the
 * Director of Finance, tiers 1-2); everyone else reads.
 */

const field = 'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const primary = 'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';
const secondary = 'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50';

function todayLocal(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date());
}

const orderWords = (o: { order_type: string | null; signed_date: string | null }) =>
  `${o.order_type ?? 'Order'}${o.signed_date ? ` of ${formatDate(o.signed_date)}` : ', date not known'}`;

/** One step in words: what happened, and the facts the step holds (never a full account number). */
export function stepSaid(s: MarshalStep, m: ClientMarshalling): string {
  const order = (id: string | null) => { const o = m.orders.find((x) => x.id === id); return o ? orderWords(o) : 'an order'; };
  const account = (id: string | null) => { const a = m.accounts.find((x) => x.id === id); return a ? `${KIND_WORDS[a.kind]} ${lastFour(a.last4)}` : 'an account'; };
  const parts: string[] = [STEP_WORDS[s.kind]];
  if (s.kind === 'bank_review' && s.review_outcome) parts.push(s.review_outcome === 'approved' ? 'approved' : s.review_outcome === 'refused' ? 'refused' : 'more documents needed');
  if (s.kind === 'documents_scanned') {
    if (s.letter_brought) parts.push('the Allied letter');
    if (s.certified_copies) parts.push(`${s.certified_copies} certified ${s.certified_copies === 1 ? 'copy' : 'copies'}`);
    if (s.other_text) parts.push(s.other_text);
  }
  const ordered = s.court_order_id ? stepOrderSaid(s.kind, order(s.court_order_id)) : null;
  if (ordered) parts.push(ordered);
  if (s.kind === 'check_received') {
    parts.push(`$${s.amount}`);
    if (s.is_partial) parts.push(`a partial, up to ${order(s.court_order_id)}${s.approx_remaining ? `; about $${s.approx_remaining} left at the bank` : ''}`);
    else if (s.is_last) parts.push('the last check from this bank');
    parts.push(s.deposited ? 'deposited' : 'not yet deposited');
  }
  if (s.kind === 'check_deposited' || s.kind === 'internal_move') parts.push(`into ${account(s.account_id)}`);
  if (s.under_order_id) parts.push(`under ${order(s.under_order_id)}`);
  return parts.join(' · ');
}

export function MarshallingPanel({ clientId }: { clientId: string }) {
  const q = useClientMarshalling(clientId);
  const [said, setSaid] = useState<string | null>(null);
  const [adding, setAdding] = useState<'none' | 'bank' | 'guardianship'>('none');
  if (q.error && !q.data) return <div className="px-4 pb-3"><Refusal error={q.error} /></div>;
  if (!q.data) return <p className="px-4 pb-3 text-neutral-600">Loading the marshalling…</p>;
  const m = q.data;
  const banks = m.institutions;
  // F-102 (175): "No accounts found" only while no bank is listed; A-#576: where adding a bank sits
  const listed = banks.filter((i) => !i.withdrawn);
  const place = addBankPlace(m.deadlines[0], banks);
  const guardianship = m.accounts.filter((a) => a.role === 'guardianship' && !a.marshal_institution_id);
  return (
    <div>
      {said && <p role="status" className="px-4 pt-2 text-sm text-neutral-800">{said}</p>}
      <p className="px-4 pt-2 font-semibold text-neutral-900">{deadlineSaid(m.deadlines[0], banks, formatDate)}</p>
      {m.no_accounts && <p className="px-4 text-sm text-neutral-700">No accounts found, recorded on {formatDate(m.no_accounts.on_date)}.</p>}
      {m.tasks.length > 0 && (
        <ul className="mt-2 space-y-1 px-4 text-sm text-neutral-800" aria-label="Tasks the marshalling opened">
          {m.tasks.map((t) => <li key={t.id}>{t.title} · {t.department} · {t.status}</li>)}
        </ul>
      )}
      <ul className="mt-2 divide-y divide-neutral-100 border-t border-neutral-200">
        {banks.map((i) => (
          <li key={i.id} className="px-4 py-3">
            <Bank clientId={clientId} bank={i} m={m} onSaid={setSaid} />
          </li>
        ))}
      </ul>
      {banks.length === 0 && <p className="px-4 py-2 text-neutral-600">No bank or institution is listed for this client yet.</p>}
      {guardianship.length > 0 && (
        <section aria-labelledby={`ga-${clientId}`} className="border-t border-neutral-200 px-4 py-3">
          <h3 id={`ga-${clientId}`} className="font-semibold text-neutral-900">Guardianship accounts</h3>
          <ul className="mt-1 space-y-1 text-sm text-neutral-800">
            {guardianship.map((a) => <li key={a.id}><AccountLine clientId={clientId} account={a} mayEdit={m.may_record} onSaid={setSaid} /></li>)}
          </ul>
        </section>
      )}
      {m.may_record && (
        <div className="flex flex-wrap gap-2 border-t border-neutral-200 px-4 py-3">
          {adding === 'none' && place === 'bar' && <button type="button" className={secondary} onClick={() => setAdding('bank')}>{ADD_BANK_LABEL}</button>}
          {adding === 'none' && <button type="button" className={secondary} onClick={() => setAdding('guardianship')}>Add a guardianship account</button>}
          {adding === 'none' && listed.length === 0 && !m.no_accounts && <NoAccounts clientId={clientId} onSaid={setSaid} />}
        </div>
      )}
      {adding === 'bank' && <AddBank clientId={clientId} onDone={(x) => { setAdding('none'); if (x) setSaid(x); }} />}
      {adding === 'guardianship' && (
        <AccountForm clientId={clientId} bank={null} onDone={(x) => { setAdding('none'); if (x) setSaid(x); }} />
      )}
      {m.may_record && adding === 'none' && place === 'bottom' && (
        <div className="border-t border-neutral-200 px-4 py-3">
          <button type="button" className={secondary} aria-describedby={`new-bank-${clientId}`} onClick={() => setAdding('bank')}>{NEW_BANK_LABEL}</button>
          <p id={`new-bank-${clientId}`} className="mt-1 text-sm text-neutral-600">{NEW_BANK_HINT}</p>
        </div>
      )}
    </div>
  );
}

function accountSaid(a: MarshalAccount): string {
  return `${KIND_WORDS[a.kind]} ${lastFour(a.last4)}${a.role === 'guardianship' ? ` at ${a.institution_name}, a guardianship account` : ''}`
    + `${a.marshal_outcome ? ` · ${a.marshal_outcome}${a.marshal_outcome_on ? ` on ${formatDate(a.marshal_outcome_on)}` : ''}` : ''}`
    + `${a.withdrawn ? ' · withdrawn' : ''}`;
}

function Bank({ clientId, bank, m, onSaid }: { clientId: string; bank: MarshalInstitution; m: ClientMarshalling; onSaid: (x: string) => void }) {
  const [mode, setMode] = useState<'none' | 'step' | 'account' | 'withdraw'>('none');
  const accounts = m.accounts.filter((a) => a.marshal_institution_id === bank.id);
  const steps = m.actions.filter((s) => s.marshal_institution_id === bank.id);
  return (
    <>
      <p className="font-semibold text-neutral-900">{bank.name}</p>
      <p className="text-sm text-neutral-700">{institutionState(bank, formatDate)}</p>
      {bank.withdrawn && <p className="text-sm text-neutral-700">Withdrawn: {asSentence(bank.withdrawal_reason)}</p>}
      {accounts.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-sm text-neutral-800" aria-label={`Accounts at ${bank.name}`}>
          {accounts.map((a) => <li key={a.id}><AccountLine clientId={clientId} account={a} mayEdit={m.may_record} onSaid={onSaid} /></li>)}
        </ul>
      )}
      {steps.length > 0 && (
        <ol className="mt-2 space-y-1" aria-label={`Steps at ${bank.name}`}>
          {steps.map((s) => <li key={s.id}><Step clientId={clientId} step={s} m={m} mayEdit={m.may_record} onSaid={onSaid} /></li>)}
        </ol>
      )}
      {m.may_record && !bank.withdrawn && mode === 'none' && (
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" className={secondary} onClick={() => setMode('step')}>Record the next step</button>
          <button type="button" className={secondary} onClick={() => setMode('account')}>Add an account found here</button>
          <RecordEdit may className={secondary} choices={[
            { key: 'withdraw', label: 'Withdraw the bank: listed in error', onPick: () => setMode('withdraw') },
          ]} />
        </div>
      )}
      {mode === 'step' && <StepForm clientId={clientId} bank={bank} m={m} onDone={(x) => { setMode('none'); if (x) onSaid(x); }} />}
      {mode === 'account' && <AccountForm clientId={clientId} bank={bank} onDone={(x) => { setMode('none'); if (x) onSaid(x); }} />}
      {mode === 'withdraw' && <WithdrawBank clientId={clientId} bank={bank} onDone={(x) => { setMode('none'); if (x) onSaid(x); }} />}
    </>
  );
}

function Step({ clientId, step, m, mayEdit, onSaid }: { clientId: string; step: MarshalStep; m: ClientMarshalling; mayEdit: boolean; onSaid: (x: string) => void }) {
  const [withdrawing, setWithdrawing] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [why, setWhy] = useState('');
  const withdraw = useWithdrawStep(clientId);
  return (
    <div className="rounded-lg border border-neutral-200 px-3 py-2 text-[0.9375rem]">
      <p className="text-neutral-900"><span className="tabular-nums">{formatDate(step.on_date)}</span> · {stepSaid(step, m)}</p>
      {step.withdrawn && <p className="text-sm text-neutral-700">Withdrawn: {asSentence(step.withdrawal_reason)} It counts for nothing.</p>}
      {!withdrawing && !correcting && (
        <RecordEdit may={mayEdit && !step.withdrawn} className={secondary} choices={[
          { key: 'correct', label: 'Correct', onPick: () => setCorrecting(true) },
          { key: 'withdraw', label: step.kind === 'check_received' ? 'Withdraw: returned, voided or recorded in error' : 'Withdraw: recorded in error',
            onPick: () => setWithdrawing(true) },
        ]} />
      )}
      {correcting && (
        <CorrectStep clientId={clientId} step={step} onDone={(x) => { setCorrecting(false); if (x) onSaid(x); }} />
      )}
      {withdrawing && (
        <form className="mt-2 space-y-2" onSubmit={(e) => {
          e.preventDefault();
          if (!why.trim()) return;
          withdraw.mutate({ stepId: step.id, reason: why.trim() }, { onSuccess: () => { setWithdrawing(false); onSaid('Withdrawn. What it marshalled is open again.'); } });
        }}>
          <label className={label} htmlFor={`msw-${step.id}`}>Why</label>
          <input id={`msw-${step.id}`} className={field} value={why} maxLength={2000} onChange={(e) => setWhy(e.target.value)} />
          {withdraw.error && <Refusal error={withdraw.error} />}
          <div className="flex flex-wrap gap-2">
            <button type="submit" className={primary} disabled={!why.trim() || withdraw.isPending}>Withdraw</button>
            <button type="button" className={secondary} onClick={() => setWithdrawing(false)}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * Correct a step (166's marshal_action_edit(); A-#391 D3: the recording roles, with a reason). A whole version of the
 * step's fields, the changed ones replaced; the earlier version is kept. It says first what the correction changes.
 */
function CorrectStep({ clientId, step, onDone }: { clientId: string; step: MarshalStep; onDone: (x: string | null) => void }) {
  const [f, setF] = useState({ on_date: step.on_date, amount: formatMoneyInput(step.amount ?? ''), last: step.is_last ?? false,
    outcome: step.review_outcome ?? 'approved' });
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  // F-100: a check's amount is required, and only money is sent
  const ready = useReasonReady(reason, text) && (step.kind !== 'check_received' || moneyText(f.amount) !== null);
  const correct = useCorrectStep(clientId);
  const id = `msc-${step.id}`;
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3" onSubmit={(e) => {
      e.preventDefault();
      if (!ready || !f.on_date) return;
      // a whole version: every field the step holds, the changed ones replaced
      const fields: Record<string, unknown> = { ...step.fields, on_date: f.on_date };
      if (step.kind === 'check_received') { fields.amount = moneyText(f.amount); if (!step.is_partial) fields.is_last = f.last; }
      if (step.kind === 'bank_review') fields.review_outcome = f.outcome;
      correct.mutate({ stepId: step.id, fields, reason, ...(text.trim() ? { reason_text: text.trim() } : {}) },
        { onSuccess: () => onDone('Corrected. The bank and "Marshal client accounts" follow the corrected step.') });
    }}>
      <h3 className="font-semibold text-neutral-900">Correct: {STEP_WORDS[step.kind].toLowerCase()}</h3>
      <p className={hint}>{stepCorrectionSays(step.kind)}</p>
      <div>
        <label className={label} htmlFor={`${id}-date`}>{step.kind === 'appointment_set' ? 'The day of the appointment' : 'The day it happened'}</label>
        <input id={`${id}-date`} type="date" className={field} value={f.on_date}
               max={step.kind === 'appointment_set' ? undefined : todayLocal()} onChange={set('on_date')} />
      </div>
      {step.kind === 'check_received' && (
        <>
          <div>
            <label className={label} htmlFor={`${id}-amount`}>Amount</label>
            <MoneyBox id={`${id}-amount`} className={field} value={f.amount} onChange={(v) => setF((x) => ({ ...x, amount: v }))} words={false} />
          </div>
          {!step.is_partial && (
            <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
              <input type="checkbox" className="mt-1 size-5 shrink-0" checked={f.last} onChange={set('last')} />
              <span className="text-neutral-900">This is the last check from this bank</span>
            </label>
          )}
        </>
      )}
      {step.kind === 'bank_review' && (
        <div>
          <label className={label} htmlFor={`${id}-outcome`}>The bank</label>
          <select id={`${id}-outcome`} className={field} value={f.outcome} onChange={set('outcome')}>
            <option value="approved">Approved</option>
            <option value="more documents needed">Asked for more documents</option>
            <option value="refused">Refused</option>
          </select>
        </div>
      )}
      <ReasonFields reason={reason} text={text} setReason={setReason} setText={setText} idPrefix={id} />
      {correct.error && <Refusal error={correct.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ready || !f.on_date || correct.isPending}>
          {correct.isPending ? 'Saving…' : 'Save the correction'}
        </button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

/** An account, by its last four, with Correct for the recording roles. */
function AccountLine({ clientId, account, mayEdit, onSaid }: { clientId: string; account: MarshalAccount; mayEdit: boolean; onSaid: (x: string) => void }) {
  const [correcting, setCorrecting] = useState(false);
  return (
    <div>
      <span>{accountSaid(account)}</span>
      {!correcting && (
        <span className="ml-2 inline-block align-middle">
          <RecordEdit may={mayEdit && !account.withdrawn} className={secondary} choices={[
            { key: 'correct', label: 'Correct the account', onPick: () => setCorrecting(true) },
          ]} />
        </span>
      )}
      {correcting && <CorrectAccount clientId={clientId} account={account} onDone={(x) => { setCorrecting(false); if (x) onSaid(x); }} />}
    </div>
  );
}

/** Correct an account (166's guardianship_account_edit()): its kind, last four and how it was found; with a reason. */
function CorrectAccount({ clientId, account, onDone }: { clientId: string; account: MarshalAccount; onDone: (x: string | null) => void }) {
  const [f, setF] = useState({ kind: account.kind, last4: account.last4, title: account.title_as_found ?? '',
    found_on: account.found_on ?? '', found_how: account.found_how ?? '' });
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const ready = useReasonReady(reason, text);
  const correct = useCorrectAccount(clientId);
  const id = `mac-${account.id}`;
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const ok = ready && /^\d{4}$/.test(f.last4.trim());
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3" onSubmit={(e) => {
      e.preventDefault();
      if (!ok) return;
      const fields = { ...account.fields, kind: f.kind, last4: f.last4.trim(), title_as_found: f.title.trim() || null,
        found_on: f.found_on || null, found_how: f.found_how.trim() || null };
      correct.mutate({ accountId: account.id, fields, reason, ...(text.trim() ? { reason_text: text.trim() } : {}) },
        { onSuccess: () => onDone(`Account ${lastFour(f.last4.trim())} corrected.`) });
    }}>
      <h3 className="font-semibold text-neutral-900">Correct {KIND_WORDS[account.kind]} {lastFour(account.last4)}</h3>
      <p className={hint}>{ACCOUNT_CORRECTION_SAYS}</p>
      <div>
        <label className={label} htmlFor={`${id}-kind`}>Kind</label>
        <select id={`${id}-kind`} className={field} value={f.kind} onChange={set('kind')}>
          {(Object.keys(KIND_WORDS) as AccountKind[]).map((k) => <option key={k} value={k}>{KIND_WORDS[k]}</option>)}
        </select>
      </div>
      <div>
        <label className={label} htmlFor={`${id}-last4`}>The last four digits</label>
        <input id={`${id}-last4`} inputMode="numeric" maxLength={4} className={field} value={f.last4} onChange={set('last4')} />
      </div>
      {account.role === 'held at marshalling' && (
        <>
          <div>
            <label className={label} htmlFor={`${id}-title`}>Its title as found</label>
            <input id={`${id}-title`} className={field} value={f.title} maxLength={300} onChange={set('title')} />
          </div>
          <div>
            <label className={label} htmlFor={`${id}-found`}>Found on</label>
            <input id={`${id}-found`} type="date" className={field} value={f.found_on} max={todayLocal()} onChange={set('found_on')} />
          </div>
          <div>
            <label className={label} htmlFor={`${id}-how`}>How it was found</label>
            <input id={`${id}-how`} className={field} value={f.found_how} maxLength={500} onChange={set('found_how')} />
          </div>
        </>
      )}
      <ReasonFields reason={reason} text={text} setReason={setReason} setText={setText} idPrefix={id} />
      {correct.error && <Refusal error={correct.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ok || correct.isPending}>{correct.isPending ? 'Saving…' : 'Save the correction'}</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function StepForm({ clientId, bank, m, onDone }: { clientId: string; bank: MarshalInstitution; m: ClientMarshalling; onDone: (x: string | null) => void }) {
  const [kind, setKind] = useState<StepKind>('appointment_set');
  const [f, setF] = useState({ on_date: '', outcome: 'approved', letter: false, copies: '', other: '', amount: '', last: false,
    partial: false, remaining: '', order: '', under: '', account: '', check: '', upload: '' });
  const [outcomes, setOutcomes] = useState<Record<string, AccountOutcome | ''>>({});
  const record = useRecordStep(clientId);
  const folders = useClientFolders(clientId);
  const sending = useRef(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));
  const id = `mstep-${bank.id}`;
  const held = m.accounts.filter((a) => a.marshal_institution_id === bank.id && a.role === 'held at marshalling' && !a.withdrawn);
  const guardianshipHere = m.accounts.filter((a) => a.role === 'guardianship' && !a.withdrawn && a.marshal_institution_id === bank.id);
  const guardianshipAny = m.accounts.filter((a) => a.role === 'guardianship' && !a.withdrawn);
  const checksInHand = m.actions.filter((s) => s.marshal_institution_id === bank.id && s.kind === 'check_received' && !s.withdrawn && !s.deposited);
  const further = m.orders.filter((o) => o.authorizes_further_marshalling);
  const refused = m.actions.some((s) => s.marshal_institution_id === bank.id && s.kind === 'bank_review' && s.review_outcome === 'refused' && !s.withdrawn);
  const clean = (folders.data?.uploads ?? []).filter((u) => u.openable);
  const fullyMarshalled = bank.marshalled_on && !bank.partial;
  // F-84: the one builder; null while a box holds what the API would refuse, so Record stays off and nothing is sent
  const body = stepFields(kind, f, refused);
  const ready = !!f.on_date && body !== null;
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ready || !body || sending.current) return;
            sending.current = true;
            const accounts = kind === 'return_visit'
              ? Object.entries(outcomes).filter(([, o]) => o).map(([account_id, outcome]) => ({ account_id, outcome: outcome as AccountOutcome }))
              : undefined;
            record.mutate({ institutionId: bank.id, kind, fields: body, ...(accounts ? { accounts } : {}) }, {
              onSuccess: () => onDone(`Recorded: ${STEP_WORDS[kind].toLowerCase()}.`),
              onError: () => { sending.current = false; },
            });
          }}>
      <h3 className="font-semibold text-neutral-900">Record the next step at {bank.name}</h3>
      <div>
        <label className={label} htmlFor={`${id}-kind`}>What happened</label>
        <select id={`${id}-kind`} className={field} value={kind} onChange={(e) => setKind(e.target.value as StepKind)}>
          {STEP_ORDER.map((k) => <option key={k} value={k}>{STEP_WORDS[k]}</option>)}
        </select>
        <p className={hint}>{STEP_HINTS[kind]}</p>
        {kind === 'check_received' && fullyMarshalled && (
          <p className={hint}>This bank is already marshalled by its last check: a further check here is kept as history and changes nothing.</p>
        )}
        {kind === 'check_received' && bank.partial && (
          <p className={hint}>This bank was partly marshalled under an order: a further check is recorded under the later order that allows it.</p>
        )}
      </div>
      <div>
        <label className={label} htmlFor={`${id}-date`}>{kind === 'appointment_set' ? 'The day of the appointment' : 'The day it happened'}</label>
        <input id={`${id}-date`} type="date" className={field} value={f.on_date}
               max={kind === 'appointment_set' ? undefined : todayLocal()} onChange={set('on_date')} />
      </div>
      {kind === 'documents_scanned' && (
        <>
          <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
            <input type="checkbox" className="mt-1 size-5 shrink-0" checked={f.letter} onChange={set('letter')} />
            <span className="text-neutral-900">The Allied letter was brought</span>
          </label>
          <div>
            <label className={label} htmlFor={`${id}-copies`}>Certified copies brought</label>
            <input id={`${id}-copies`} inputMode="numeric" className={field} value={f.copies} onChange={set('copies')}
                   aria-invalid={!copiesOk(f.copies)} aria-describedby={`${id}-copies-hint`} />
            <p id={`${id}-copies-hint`} className={hint}>{COPIES_WORDS}</p>
          </div>
          <div>
            <label className={label} htmlFor={`${id}-other`}>Other documents</label>
            <input id={`${id}-other`} className={field} value={f.other} maxLength={1000} onChange={set('other')} />
            <p className={hint}>Never a full account number: an account is known by its last four.</p>
          </div>
        </>
      )}
      {kind === 'bank_review' && (
        <div>
          <label className={label} htmlFor={`${id}-outcome`}>The bank</label>
          <select id={`${id}-outcome`} className={field} value={f.outcome} onChange={set('outcome')}>
            <option value="approved">Approved</option>
            <option value="more documents needed">Asked for more documents</option>
            <option value="refused">Refused</option>
          </select>
        </div>
      )}
      {(kind === 'documents_scanned' || (kind === 'bank_review' && refused && f.outcome !== 'refused')) && m.orders.length > 0 && (
        <div>
          <label className={label} htmlFor={`${id}-order`}>{kind === 'bank_review' ? 'The order the bank acted on' : 'Whose certified copies'}</label>
          <select id={`${id}-order`} className={field} value={f.order} onChange={set('order')}>
            <option value="">Not known</option>
            {m.orders.map((o) => <option key={o.id} value={o.id}>{orderWords(o)}</option>)}
          </select>
        </div>
      )}
      {kind === 'return_visit' && (
        held.length === 0 ? <p className={hint}>No account found at this bank is listed: add the accounts first.</p> : (
          <fieldset className="space-y-2">
            <legend className={label}>Each account&rsquo;s outcome</legend>
            {held.map((a) => (
              <div key={a.id}>
                <label className="block text-sm text-neutral-800" htmlFor={`${id}-acct-${a.id}`}>{KIND_WORDS[a.kind]} {lastFour(a.last4)}</label>
                <select id={`${id}-acct-${a.id}`} className={field} value={outcomes[a.id] ?? ''}
                        onChange={(e) => setOutcomes((x) => ({ ...x, [a.id]: e.target.value as AccountOutcome | '' }))}>
                  <option value="">Not recorded</option>
                  <option value="Closed">Closed</option>
                  <option value="Retitled">Retitled</option>
                  <option value="Not the client's">Not the client&rsquo;s</option>
                  <option value="Already closed when found">Already closed when found</option>
                </select>
              </div>
            ))}
          </fieldset>
        )
      )}
      {kind === 'check_received' && (
        <>
          <div>
            <label className={label} htmlFor={`${id}-amount`}>Amount</label>
            <MoneyBox id={`${id}-amount`} className={field} value={f.amount} onChange={(v) => setF((x) => ({ ...x, amount: v }))} />
          </div>
          <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
            <input type="checkbox" className="mt-1 size-5 shrink-0" checked={f.last || f.partial} disabled={f.partial} onChange={set('last')} />
            <span className="text-neutral-900">This is the last check from this bank</span>
          </label>
          <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
            <input type="checkbox" className="mt-1 size-5 shrink-0" checked={f.partial} onChange={set('partial')} />
            <span className="text-neutral-900">A partial: up to the amount a court order allows</span>
          </label>
          {f.partial && (
            <>
              <div>
                <label className={label} htmlFor={`${id}-limit`}>The order that set the limit</label>
                <select id={`${id}-limit`} className={field} value={f.order} onChange={set('order')}>
                  <option value="">Choose the order</option>
                  {m.orders.map((o) => <option key={o.id} value={o.id}>{orderWords(o)}</option>)}
                </select>
              </div>
              <div>
                <label className={label} htmlFor={`${id}-left`}>About how much is left at the bank</label>
                <MoneyBox id={`${id}-left`} className={field} value={f.remaining} onChange={(v) => setF((x) => ({ ...x, remaining: v }))}
                          lead={'Shown as "about". Nothing follows from it: only a later order that allows more opens new work.'} />
              </div>
            </>
          )}
        </>
      )}
      {kind === 'check_deposited' && (
        <>
          <div>
            <label className={label} htmlFor={`${id}-check`}>The check</label>
            <select id={`${id}-check`} className={field} value={f.check} onChange={set('check')}>
              <option value="">Choose the check</option>
              {checksInHand.map((c) => <option key={c.id} value={c.id}>{formatDate(c.on_date)} · ${c.amount}</option>)}
            </select>
          </div>
          <AccountChoice id={`${id}-into`} label="Deposited into" accounts={guardianshipAny} value={f.account} onChange={set('account')} />
        </>
      )}
      {kind === 'internal_move' && (
        <AccountChoice id={`${id}-moved`} label="Moved into" accounts={guardianshipHere} value={f.account} onChange={set('account')}
                       none="No guardianship account at this bank is listed: add it first, as a guardianship account at this bank." />
      )}
      {kind === 'statements_received' && (
        <div>
          <label className={label} htmlFor={`${id}-doc`}>The statements or the closing letter</label>
          <select id={`${id}-doc`} className={field} value={f.upload} onChange={set('upload')}>
            <option value="">Choose the document</option>
            {clean.map((u) => <option key={u.id} value={u.id}>{u.filename}</option>)}
          </select>
          <p className={hint}>Upload it to the client&rsquo;s documents first; it can be chosen once its scan says clean.</p>
        </div>
      )}
      {['check_received', 'statements_received', 'internal_move'].includes(kind) && further.length > 0 && (
        <div>
          <label className={label} htmlFor={`${id}-under`}>Recorded under a later order</label>
          <select id={`${id}-under`} className={field} value={f.under} onChange={set('under')}>
            <option value="">No</option>
            {further.map((o) => <option key={o.id} value={o.id}>{orderWords(o)}</option>)}
          </select>
          <p className={hint}>Further funds under an order are history: &ldquo;Marshal client accounts&rdquo; stays as it is.</p>
        </div>
      )}
      {record.error && <Refusal error={record.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ready || record.isPending}>{record.isPending ? 'Recording…' : 'Record'}</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function AccountChoice({ id, label: words, accounts, value, onChange, none }: {
  id: string; label: string; accounts: MarshalAccount[]; value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void; none?: string;
}) {
  if (accounts.length === 0) return <p className={hint}>{none ?? 'No guardianship account is listed: add it first.'}</p>;
  return (
    <div>
      <label className={label} htmlFor={id}>{words}</label>
      <select id={id} className={field} value={value} onChange={onChange}>
        <option value="">Choose the account</option>
        {accounts.map((a) => <option key={a.id} value={a.id}>{KIND_WORDS[a.kind]} {lastFour(a.last4)} at {a.institution_name}</option>)}
      </select>
    </div>
  );
}

function AddBank({ clientId, onDone }: { clientId: string; onDone: (x: string | null) => void }) {
  const [name, setName] = useState('');
  const add = useAddInstitution(clientId);
  return (
    <form className="mx-4 mb-3 space-y-3 rounded-lg border border-neutral-300 p-3" onSubmit={(e) => {
      e.preventDefault();
      if (!name.trim()) return;
      add.mutate({ name: name.trim() }, { onSuccess: () => onDone(`Added ${name.trim()}.`) });
    }}>
      <h3 className="font-semibold text-neutral-900">Add a bank or institution</h3>
      <div>
        <label className={label} htmlFor="mbank-name">Its name</label>
        <input id="mbank-name" className={field} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
      </div>
      {add.error && <Refusal error={add.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!name.trim() || add.isPending}>Add</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function AccountForm({ clientId, bank, onDone }: { clientId: string; bank: MarshalInstitution | null; onDone: (x: string | null) => void }) {
  const [f, setF] = useState({ kind: 'checking' as AccountKind, last4: '', title: '', found_on: '', found_how: '', bankName: '', moved: false });
  const add = useAddAccount(clientId);
  const id = `macct-${bank?.id ?? 'g'}`;
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));
  const ok = /^\d{4}$/.test(f.last4.trim()) && (bank || f.bankName.trim());
  return (
    <form className={`${bank ? 'mt-2' : 'mx-4 mb-3'} space-y-3 rounded-lg border border-neutral-300 p-3`} onSubmit={(e) => {
      e.preventDefault();
      if (!ok) return;
      const moved = bank !== null && f.moved;
      add.mutate({
        institution_id: bank ? bank.id : null, institution_name: bank ? null : f.bankName.trim(), kind: f.kind,
        last4: f.last4.trim(), title_as_found: moved ? null : f.title.trim() || null, found_on: moved ? null : f.found_on || null,
        found_how: moved ? null : f.found_how.trim() || null,
        role: bank && !moved ? 'held at marshalling' : 'guardianship',
      }, { onSuccess: () => onDone(`Account ${lastFour(f.last4.trim())} added.`) });
    }}>
      <h3 className="font-semibold text-neutral-900">{bank ? `An account found at ${bank.name}` : 'A guardianship account'}</h3>
      {!bank && (
        <div>
          <label className={label} htmlFor={`${id}-bank`}>The bank</label>
          <input id={`${id}-bank`} className={field} value={f.bankName} maxLength={200} onChange={set('bankName')} />
        </div>
      )}
      <div>
        <label className={label} htmlFor={`${id}-kind`}>Kind</label>
        <select id={`${id}-kind`} className={field} value={f.kind} onChange={set('kind')}>
          {(Object.keys(KIND_WORDS) as AccountKind[]).map((k) => <option key={k} value={k}>{KIND_WORDS[k]}</option>)}
        </select>
      </div>
      <div>
        <label className={label} htmlFor={`${id}-last4`}>The last four digits</label>
        <input id={`${id}-last4`} inputMode="numeric" maxLength={4} className={field} value={f.last4} onChange={set('last4')} />
        <p className={hint}>Exactly four digits, shown as &ldquo;...1234&rdquo;. Never the full number.</p>
      </div>
      {bank && (
        <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
          <input type="checkbox" className="mt-1 size-5 shrink-0" checked={f.moved} onChange={set('moved')} />
          <span className="text-neutral-900">This is the new guardianship account at this bank that the money moved into</span>
        </label>
      )}
      {bank && !f.moved && (
        <>
          <div>
            <label className={label} htmlFor={`${id}-title`}>Its title as found</label>
            <input id={`${id}-title`} className={field} value={f.title} maxLength={300} onChange={set('title')} />
          </div>
          <div>
            <label className={label} htmlFor={`${id}-found`}>Found on</label>
            <input id={`${id}-found`} type="date" className={field} value={f.found_on} max={todayLocal()} onChange={set('found_on')} />
            <p className={hint}>An account found after the bank was marshalled opens it again.</p>
          </div>
          <div>
            <label className={label} htmlFor={`${id}-how`}>How it was found</label>
            <input id={`${id}-how`} className={field} value={f.found_how} maxLength={500} onChange={set('found_how')} />
          </div>
        </>
      )}
      {add.error && <Refusal error={add.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ok || add.isPending}>Add the account</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function WithdrawBank({ clientId, bank, onDone }: { clientId: string; bank: MarshalInstitution; onDone: (x: string | null) => void }) {
  const [why, setWhy] = useState('');
  const withdraw = useWithdrawInstitution(clientId);
  return (
    <form className="mt-2 space-y-2 rounded-lg border border-neutral-300 p-3" onSubmit={(e) => {
      e.preventDefault();
      if (!why.trim()) return;
      withdraw.mutate({ institutionId: bank.id, reason: why.trim() }, { onSuccess: () => onDone(`${bank.name} withdrawn. It counts for nothing.`) });
    }}>
      <label className={label} htmlFor={`mbw-${bank.id}`}>Why it was listed in error</label>
      <input id={`mbw-${bank.id}`} className={field} value={why} maxLength={2000} onChange={(e) => setWhy(e.target.value)} />
      {withdraw.error && <Refusal error={withdraw.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!why.trim() || withdraw.isPending}>Withdraw</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function NoAccounts({ clientId, onSaid }: { clientId: string; onSaid: (x: string) => void }) {
  const [on, setOn] = useState('');
  const record = useNoAccountsFound(clientId);
  return (
    <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => {
      e.preventDefault();
      if (!on) return;
      record.mutate({ on_date: on }, { onSuccess: () => onSaid('No accounts found, recorded. "Marshal client accounts" is complete.') });
    }}>
      <div>
        <label className={label} htmlFor="mnoacct">No accounts found, on</label>
        <input id="mnoacct" type="date" className={field} value={on} max={todayLocal()} onChange={(e) => setOn(e.target.value)} />
      </div>
      <button type="submit" className={secondary} disabled={!on || record.isPending}>Record &ldquo;No accounts found&rdquo;</button>
      {record.error && <Refusal error={record.error} />}
    </form>
  );
}
